import { useCallback } from 'react';
import { findComponentById } from '../pages/utils/pageSnapshot';
import { exampleProps } from '../pages/config/exampleProps';
import { applyGlobalTheme } from '../pages/utils/apis';

/**
 * useApplyAiActions
 *
 * Translates the AI's action array into pageBuilderReducer dispatches.
 *
 * @param {Function} dispatch    - The pageBuilderReducer dispatch function
 * @param {Array}    rows        - Current rows state (for ID lookups)
 * @param {string}   subdomain   - Account key for global theme API calls
 * @returns {Function}           - applyAiActions(actions: Array) => void
 */
export function useApplyAiActions(dispatch, rows, subdomain) {
  const applyAiActions = useCallback(
    (actions) => {
      if (!Array.isArray(actions) || actions.length === 0) return;

      actions.forEach((action) => {
        try {
          switch (action.type) {

            // ── Global theme change via CSS variables ───────────────
            case 'UPDATE_GLOBAL_THEME': {
              const { primary_color, secondary_color, text_color, background_color } = action;

              if (!subdomain) {
                console.warn('[AI] UPDATE_GLOBAL_THEME: no subdomain available');
                break;
              }

              // Fire and forget — the CSS is saved to meta_data on the server
              // The page will reflect it on next load, or we can force a style refresh
              applyGlobalTheme({
                accountKey: subdomain,
                primaryColor: primary_color,
                secondaryColor: secondary_color,
                textColor: text_color,
                backgroundColor: background_color,
              })
                .then(() => {
                  // Inject the CSS immediately into the page without a reload
                  injectThemeCSS({ primary_color, secondary_color, text_color, background_color });
                })
                .catch((err) => console.error('[AI] Theme apply failed:', err));
              break;
            }

            // ── Add a brand new row+section+columns+components ──────
            case 'ADD_FULL_SECTION': {
              const { columns = [], styles = {}, position = 'end', referenceRowId = null } = action;

              if (!columns.length) {
                dispatch({ type: 'ADD_ROW' });
                break;
              }

              const builtColumns = columns.map((colDef) => ({
                class: colDef.class || 'col-lg-12',
                styles: colDef.styles || {},
                components: (colDef.components || []).map((comp) => {
                  const defaults = exampleProps[comp.type] || {};
                  return {
                    type: comp.type,
                    props: { ...defaults, ...(comp.props || {}) },
                    disabled: false,
                  };
                }),
              }));

              dispatch({
                type: 'ADD_FULL_SECTION',
                payload: {
                  columns: builtColumns,
                  styles,
                  position,
                  referenceRowId,
                },
              });
              break;
            }

            // ── Add a component to an existing column ───────────────
            case 'ADD_COMPONENT_TO_EXISTING': {
              const { targetSectionId, targetColumnIdx, component } = action;
              if (!targetSectionId || targetColumnIdx === undefined || !component?.type) {
                console.warn('[AI] ADD_COMPONENT_TO_EXISTING: missing required fields', action);
                break;
              }

              const defaults = exampleProps[component.type] || {};
              const mergedProps = { ...defaults, ...(component.props || {}) };

              dispatch({
                type: 'ADD_COMPONENT_TO_COLUMN',
                payload: {
                  sectionId: targetSectionId,
                  columnIdx: targetColumnIdx,
                  component: { type: component.type, props: mergedProps },
                },
              });
              break;
            }

            // ── Update component props (content only) ──────────────
            case 'UPDATE_COMPONENT_PROPS': {
              const { componentId, props } = action;
              if (!componentId || !props) break;

              const found = findComponentById(rows, componentId);
              if (!found) {
                console.warn(`[AI] UPDATE_COMPONENT_PROPS: component "${componentId}" not found`);
                break;
              }

              const updatedComponent = {
                ...found.component,
                props: { ...found.component.props, ...props },
              };

              dispatch({
                type: 'UPDATE_COMPONENT_IN_COLUMN',
                payload: {
                  sectionId: found.sectionId,
                  columnIdx: found.columnIdx,
                  componentIdx: found.componentIdx,
                  updatedComponent,
                },
              });
              break;
            }

            // ── Update component styles (visual styles) ─────────────
            case 'UPDATE_COMPONENT_STYLES': {
              const { componentId, breakpoint = 'desktop', styleSection, styles: newStyles } = action;
              if (!componentId || !styleSection || !newStyles) {
                console.warn('[AI] UPDATE_COMPONENT_STYLES: missing required fields', action);
                break;
              }

              const found = findComponentById(rows, componentId);
              if (!found) {
                console.warn(`[AI] UPDATE_COMPONENT_STYLES: component "${componentId}" not found`);
                break;
              }

              // Merge into the existing styles structure:
              // props.styles.{breakpoint}.{styleSection} = { ...existing, ...newStyles }
              const existingProps = found.component.props || {};
              const existingStyles = existingProps.styles || {};
              const existingBreakpoint = existingStyles[breakpoint] || {};
              const existingSection = existingBreakpoint[styleSection] || {};

              const updatedComponent = {
                ...found.component,
                props: {
                  ...existingProps,
                  styles: {
                    ...existingStyles,
                    [breakpoint]: {
                      ...existingBreakpoint,
                      [styleSection]: {
                        ...existingSection,
                        ...newStyles,
                      },
                    },
                  },
                },
              };

              dispatch({
                type: 'UPDATE_COMPONENT_IN_COLUMN',
                payload: {
                  sectionId: found.sectionId,
                  columnIdx: found.columnIdx,
                  componentIdx: found.componentIdx,
                  updatedComponent,
                },
              });
              break;
            }

            // ── Delete a component ──────────────────────────────────
            case 'DELETE_COMPONENT': {
              const { componentId, sectionId, columnIdx, componentIdx } = action;

              const found = componentId ? findComponentById(rows, componentId) : null;

              const resolvedSectionId    = found?.sectionId    ?? sectionId;
              const resolvedColumnIdx    = found?.columnIdx    ?? columnIdx;
              const resolvedComponentIdx = found?.componentIdx ?? componentIdx;

              if (
                resolvedSectionId === undefined ||
                resolvedColumnIdx === undefined ||
                resolvedComponentIdx === undefined
              ) {
                console.warn('[AI] DELETE_COMPONENT: could not resolve component location');
                break;
              }

              dispatch({
                type: 'REMOVE_COMPONENT_FROM_COLUMN',
                payload: {
                  sectionId: resolvedSectionId,
                  columnIdx: resolvedColumnIdx,
                  componentIdx: resolvedComponentIdx,
                  isMainToSub: false,
                  isMainToMain: false,
                  tempFromSectionId: null,
                  isSecton: false,
                  columnIndex: resolvedColumnIdx,
                },
              });
              break;
            }

            // ── Change layout of an existing section ────────────────
            // Restructures columns while preserving existing components
            case 'CHANGE_LAYOUT': {
              const { rowId, sectionId, columns } = action;
              if (!rowId || !sectionId || !Array.isArray(columns) || !columns.length) {
                console.warn('[AI] CHANGE_LAYOUT: missing required fields', action);
                break;
              }
              dispatch({
                type: 'CHANGE_LAYOUT',
                payload: { rowId, sectionId, columns },
              });
              break;
            }

            // ── Remove a single row ─────────────────────────────────
            case 'REMOVE_ROW': {
              const { rowId } = action;
              if (!rowId) {
                console.warn('[AI] REMOVE_ROW: missing rowId');
                break;
              }
              dispatch({ type: 'REMOVE_ROW', payload: rowId });
              break;
            }

            // ── Remove multiple rows at once ────────────────────────
            case 'REMOVE_MULTIPLE_ROWS': {
              const { rowIds } = action;
              if (!Array.isArray(rowIds) || rowIds.length === 0) {
                console.warn('[AI] REMOVE_MULTIPLE_ROWS: missing rowIds');
                break;
              }
              // Dispatch one REMOVE_ROW per rowId
              rowIds.forEach((rid) => {
                if (rid) dispatch({ type: 'REMOVE_ROW', payload: rid });
              });
              break;
            }

            // ── Update row-level styles ─────────────────────────────
            case 'UPDATE_ROW_STYLES': {
              const { rowId, styles } = action;
              if (!rowId || !styles) break;

              dispatch({
                type: 'UPDATE_STYLES',
                payload: {
                  target: { id: rowId },
                  styles,
                },
              });
              break;
            }

            // ── Update section-level styles ─────────────────────────
            case 'UPDATE_SECTION_STYLES': {
              const { rowId, sectionId, styles } = action;
              if (!rowId || !sectionId || !styles) break;

              dispatch({
                type: 'UPDATE_STYLES',
                payload: {
                  target: { entityType: 'section', rowId, id: sectionId },
                  styles: { styles, columns: undefined },
                },
              });
              break;
            }

            // ── CLARIFY — no builder change needed ──────────────────
            case 'CLARIFY':
              break;

            // ── UPDATE_COMPONENT_STYLES is handled above ────────────
            // (listed here to avoid the default warning)
            default:
              console.warn(`[AI] Unknown action type: ${action.type}`);
          }
        } catch (err) {
          console.error(`[AI] Error applying action "${action.type}":`, err, action);
        }
      });
    },
    [dispatch, rows, subdomain]
  );

  return applyAiActions;
}

/**
 * Immediately inject theme CSS into the page without a reload.
 * Creates/updates a <style id="ai-theme"> tag in <head>.
 */
function injectThemeCSS({ primary_color, secondary_color, text_color, background_color }) {
  const vars = [];
  if (primary_color)    vars.push(`  --theme-primary: ${primary_color};`);
  if (secondary_color)  vars.push(`  --theme-secondary: ${secondary_color};`);
  if (text_color)       vars.push(`  --theme-text: ${text_color};`);
  if (background_color) vars.push(`  --theme-bg: ${background_color};`);

  if (!vars.length) return;

  const css = `
/* AI Theme Variables */
:root {
${vars.join('\n')}
}
h1, h2, h3, h4, h5, h6 {
  color: var(--theme-text, inherit) !important;
}
.btn-primary, button[class*="primary"] {
  background-color: var(--theme-primary, #007bff) !important;
  border-color: var(--theme-primary, #007bff) !important;
}
body {
  background-color: var(--theme-bg, transparent) !important;
}
p, span {
  color: var(--theme-text, inherit);
}
`;

  let styleTag = document.getElementById('ai-theme');
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = 'ai-theme';
    document.head.appendChild(styleTag);
  }
  styleTag.textContent = css;
}
